/**
 * @file PlanLibraryBrowser.jsx
 * @module ui/src/components/admin/PlanLibraryBrowser
 * @description
 *   Standalone Certified Plan Library browse surface. Route: /admin/plans/library.
 *   Fetches GET /api/v1/template-library/packages on mount. Presents packages in a
 *   sortable, searchable, scrollable motion-grouped table. Row click opens a detail
 *   panel. "Deploy" opens PlanLibraryDeployModal.
 *
 *   S951 architecture: ref_plan_package is the single source of truth for all
 *   display fields. Markdown content files are optional narrative enrichment for
 *   the detail panel only. Adding a new package requires only a SQL INSERT.
 *
 * @version 2.3.0
 * @since S875
 * @author data-fit LLC · DecisionFlow · S951
 *
 * @css-contract
 *   Named classes: .modifier-manager-card, .modifier-manager-header,
 *   .modifier-manager-header-copy, .modifier-manager-body, .modifier-manager-footer,
 *   .modifier-manager-badge--*, .modifier-manager-accelerator-badge,
 *   .modifier-manager-accelerator-badge--active, .modifier-manager-inline-error,
 *   .modifier-manager-warning, .modifier-manager-info, .modifier-manager-empty-state,
 *   .modifier-manager-table, .modifier-manager-table-wrapper,
 *   .text-button, .primary-button, .secondary-button, .skeleton
 *   rgb(var(--color-*)) tokens via style= for structural layout only.
 *   Zero hardcoded hex. Zero dark: variants.
 *
 * @prohibitions
 *   - Do NOT call POST /template-library/deploy (superseded endpoint)
 *   - Do NOT use "Use" label -- must be "Deploy"
 *   - Do NOT navigate to /admin/plans/library after deploy success
 */

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Content loader — Vite glob import of ALL markdown files in content/plan-library/
// Fully generic: drop a {slug}-package.md file in the folder and it's available.
// No registration required. No UI changes needed for new packages ever.
// Key format: '../../content/plan-library/{slug}-package.md'
// ---------------------------------------------------------------------------
const CONTENT_FILES = import.meta.glob(
  '../../content/plan-library/*-package.md',
  { query: '?raw', import: 'default', eager: false }
);

async function loadPackageContent(slug) {
  if (!slug) return null;
  const key = `../../content/plan-library/${slug}-package.md`;
  const loader = CONTENT_FILES[key];
  if (!loader) return null; // file not found — caller renders DB fallback gracefully
  try {
    const raw = await loader();
    return parsePackageMarkdown(raw);
  } catch {
    return null; // parse error — same graceful fallback
  }
}

// ---------------------------------------------------------------------------
// Markdown parser -- extracts structured data from the package .md format.
// No external dependency -- pure string parsing against the known schema.
// ---------------------------------------------------------------------------
function parsePackageMarkdown(md) {
  if (!md) return null;
  const lines = md.split('\n');
  const h1 = lines.find((l) => l.startsWith('# '))?.replace(/^# /, '').trim() ?? null;
  const h2Lines = lines.filter((l) => l.startsWith('## '));
  const motionLine = h2Lines[0]?.replace(/^## /, '').trim() ?? null;
  const similarTo  = h2Lines[1]?.replace(/^## Similar to:\s*/i, '').trim() ?? null;
  const motionParts = motionLine ? motionLine.split('·').map((s) => s.trim()) : [];
  const salesMotion = motionParts[0] ?? null;
  const tier        = motionParts[1] ?? null;

  const sections = {};
  let currentSection = null;
  let currentLines = [];
  for (const line of lines) {
    if (line.startsWith('### ')) {
      if (currentSection) sections[currentSection] = currentLines.join('\n').trim();
      currentSection = line.replace(/^### /, '').replace(/^\d+\s*[—-]\s*/, '').trim();
      currentLines = [];
    } else if (currentSection) {
      currentLines.push(line);
    }
  }
  if (currentSection) sections[currentSection] = currentLines.join('\n').trim();

  const applicabilityRaw = sections['Applicability'] ?? sections['4 — Applicability'] ?? '';
  const bestForMatch        = applicabilityRaw.match(/\*\*Best for:\*\*([^*]+?)(?=\n\n|\*\*|$)/s);
  const industriesMatch     = applicabilityRaw.match(/\*\*Industries:\*\*([^*]+?)(?=\n\n|\*\*|$)/s);
  const notRecommendedMatch = applicabilityRaw.match(/\*\*Not recommended for:\*\*([^*]+?)(?=\n\n|\*\*|$)/s);

  const modulesRaw = sections['Modules Included'] ?? '';
  const moduleChips = modulesRaw
    .split('\n')
    .filter((l) => l.startsWith('|') && !l.includes('---') && !l.toLowerCase().includes('module'))
    .map((l) => { const cells = l.split('|').map((c) => c.trim()).filter(Boolean); return cells[0] ?? null; })
    .filter(Boolean);

  const governanceRaw = sections['Governance Requirements'] ?? '';
  const governanceBullets = governanceRaw
    .split('\n')
    .filter((l) => l.startsWith('- '))
    .map((l) => l.replace(/^- /, '').trim());

  return {
    displayName:    h1,
    salesMotion,
    tier,
    similarTo,
    objective:      sections['Objective'] ?? sections['1 — Objective'] ?? null,
    behaviorDrives: sections['Behavior It Drives'] ?? sections['2 — Behavior It Drives'] ?? null,
    results:        sections['Results You Can Expect'] ?? sections['3 — Results You Can Expect'] ?? null,
    bestFor:        bestForMatch?.[1]?.trim() ?? null,
    industries:     industriesMatch?.[1]?.trim() ?? null,
    notRecommended: notRecommendedMatch?.[1]?.trim() ?? null,
    designConsiderations: sections['Design Considerations'] ?? null,
    moduleChips,
    modulesRaw,
    governanceBullets,
    sections,
  };
}

// ---------------------------------------------------------------------------
// Normalize API package — reads actual API camelCase field names.
// S951: fixed to read displayName, motionCode, planTypeChips from API response.
// Adding a package to ref_plan_package requires no changes here.
// ---------------------------------------------------------------------------
function normalizePackage(raw) {
  return {
    packageCode:          raw.packageCode          ?? raw.package_code          ?? '',
    packageName:          raw.displayName          ?? raw.display_name          ?? raw.packageName ?? '',
    salesMotion:          raw.motionCode           ?? raw.motion_code           ?? raw.salesMotion ?? null,
    salesMotionGroup:     raw.motionCode           ?? raw.motion_code           ?? raw.salesMotionGroup ?? null,
    tier:                 raw.tier                 ?? raw.plan_tier             ?? null,
    complexity:           raw.complexity           ?? null,
    certificationStatus:  raw.certificationStatus  ?? raw.certification_status  ?? 'curated',
    mandate:              raw.mandate              ?? null,
    behaviorDescription:  raw.behaviorDescription  ?? raw.behavior_description  ?? null,
    expectedResults:      raw.expectedResults      ?? raw.expected_results      ?? null,
    applicability:        raw.applicability        ?? null,
    notRecommendedFor:    raw.notRecommendedFor    ?? raw.not_recommended_for   ?? null,
    primarySpine:         raw.primarySpine         ?? raw.primary_spine         ?? null,
    instanceCount:        raw.instanceCount        ?? raw.instance_count        ?? 0,
    sortOrder:            raw.sortOrder            ?? raw.sort_order            ?? 999,
    // planTypeChips: derived server-side from modifier_seed_config — DB is source of truth
    planTypeChips:        raw.planTypeChips        ?? [],
    _raw: raw,
  };
}

// ---------------------------------------------------------------------------
// Motion group ordering and DB motion_code → section heading mapping
// ---------------------------------------------------------------------------
const MOTION_GROUPS = [
  'NEW LOGO / REVENUE GROWTH',
  'EXPAND & GROW',
  'RETAIN & RENEW',
  'TEAM & COLLABORATIVE',
  'SPECIALTY',
];

const MOTION_CODE_TO_GROUP = {
  'new_logo':  'NEW LOGO / REVENUE GROWTH',
  'expand':    'EXPAND & GROW',
  'retain':    'RETAIN & RENEW',
  'team':      'TEAM & COLLABORATIVE',
  'specialty': 'SPECIALTY',
};

function groupPackages(packages) {
  const map = new Map();
  MOTION_GROUPS.forEach((g) => map.set(g, []));
  packages.forEach((pkg) => {
    const code = pkg.salesMotionGroup ?? pkg.salesMotion ?? '';
    const group = MOTION_CODE_TO_GROUP[code.toLowerCase()]
      ?? MOTION_GROUPS.find((g) => code.toUpperCase() === g || code.toUpperCase().includes(g.split(' ')[0]))
      ?? 'OTHER';
    if (!map.has(group)) map.set(group, []);
    map.get(group).push(pkg);
  });
  return [...map.entries()].filter(([, pkgs]) => pkgs.length > 0);
}

// ---------------------------------------------------------------------------
// Sortable column header
// ---------------------------------------------------------------------------
function SortTh({ label, col, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
      onClick={() => onSort(col)}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <span style={{ fontSize: '9px', color: active ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))' }}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      </span>
    </th>
  );
}

// ---------------------------------------------------------------------------
// Status badge
// ---------------------------------------------------------------------------
function StatusBadge({ instanceCount }) {
  if (!instanceCount || instanceCount === 0) {
    return <span className="modifier-manager-badge modifier-manager-badge--not-created">Not Deployed</span>;
  }
  return (
    <span className="modifier-manager-badge modifier-manager-badge--complete">
      Active · {instanceCount} {instanceCount === 1 ? 'instance' : 'instances'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------
function SkeletonRow() {
  return (
    <tr>
      <td style={{ padding: '12px 14px' }}>
        <div className="skeleton" style={{ height: '14px', width: '180px', borderRadius: '4px', marginBottom: '4px' }} />
        <div className="skeleton" style={{ height: '11px', width: '120px', borderRadius: '4px' }} />
      </td>
      <td style={{ padding: '12px 14px' }}><div className="skeleton" style={{ height: '12px', width: '80px', borderRadius: '4px' }} /></td>
      <td style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', gap: '4px' }}>
          <div className="skeleton" style={{ height: '18px', width: '70px', borderRadius: '4px' }} />
          <div className="skeleton" style={{ height: '18px', width: '80px', borderRadius: '4px' }} />
        </div>
      </td>
      <td style={{ padding: '12px 14px' }}><div className="skeleton" style={{ height: '20px', width: '60px', borderRadius: '999px' }} /></td>
      <td style={{ padding: '12px 14px' }}><div className="skeleton" style={{ height: '20px', width: '80px', borderRadius: '999px' }} /></td>
      <td style={{ padding: '12px 14px' }}><div className="skeleton" style={{ height: '28px', width: '70px', borderRadius: '8px' }} /></td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Package detail panel
// Shows markdown content when available; falls back to DB fields gracefully.
// For Tenant10k packages (no markdown file): renders mandate + DB chips + notRecommendedFor.
// ---------------------------------------------------------------------------
function PackageDetailPanel({ pkg, content, onDeploy, onClose }) {
  const name      = pkg.packageName || pkg.packageCode;
  const motion    = content?.salesMotion ?? pkg.salesMotion ?? null;
  const tier      = content?.tier        ?? pkg.tier        ?? null;
  const similarTo = content?.similarTo   ?? null;
  const chips     = pkg.planTypeChips.length > 0 ? pkg.planTypeChips : (content?.moduleChips ?? []);
  const notRec    = content?.notRecommended ?? pkg.notRecommendedFor ?? null;
  const hasContent = !!content;

  function renderText(text) {
    if (!text) return null;
    return text.replace(/\*\*([^*]+)\*\*/g, '$1').trim();
  }

  // Human-readable motion code label
  const MOTION_LABELS = {
    'new_logo': 'New Logo', 'expand': 'Expand', 'retain': 'Retain',
    'team': 'Team', 'specialty': 'Specialty',
  };
  const motionLabel = MOTION_LABELS[motion] ?? motion;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 55, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'rgb(var(--color-bg) / 0.75)' }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-label={name}
        style={{ width: '100%', maxWidth: '680px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', borderRadius: '12px', border: '1px solid rgb(var(--color-border))', background: 'rgb(var(--color-surface))', boxShadow: '0 8px 32px rgb(0 0 0 / 0.12)', overflow: 'hidden' }}
      >
        {/* Header */}
        <div className="modifier-manager-header">
          <div className="modifier-manager-header-copy" style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '15px' }}>{name}</h3>
            {similarTo && <p style={{ marginTop: '1px' }}>Similar to: {similarTo}</p>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {motionLabel && (
              <span className="modifier-manager-badge modifier-manager-badge--readonly">{motionLabel}</span>
            )}
            {tier && (
              <span className="modifier-manager-badge modifier-manager-badge--not-created">{String(tier).toUpperCase()}</span>
            )}
            <span className="modifier-manager-badge modifier-manager-badge--complete">Curated</span>
            <button type="button" className="icon-button" aria-label="Close" onClick={onClose} style={{ marginLeft: '4px' }}>×</button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Plan type chips — always shown from DB */}
          {chips.length > 0 && (
            <div>
              <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '6px' }}>
                Modules Included
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {chips.map((chip) => (
                  <span key={chip} className="modifier-manager-accelerator-badge modifier-manager-accelerator-badge--active">{chip}</span>
                ))}
              </div>
            </div>
          )}

          {/* Rich markdown content — shown when content file exists */}
          {hasContent && (
            <>
              {content.objective && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Objective</div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.6 }}>{renderText(content.objective)}</p>
                </div>
              )}
              {content.behaviorDrives && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Behavior It Drives</div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.6 }}>{renderText(content.behaviorDrives)}</p>
                </div>
              )}
              {(content.bestFor || content.industries) && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  {content.bestFor && (
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Best For</div>
                      <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.5 }}>{renderText(content.bestFor)}</p>
                    </div>
                  )}
                  {content.industries && (
                    <div>
                      <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Industries</div>
                      <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.5 }}>{renderText(content.industries)}</p>
                    </div>
                  )}
                </div>
              )}
              {content.designConsiderations && (
                <div className="modifier-manager-info">
                  <strong style={{ display: 'block', marginBottom: '2px', fontSize: '12px' }}>Design Considerations</strong>
                  <span style={{ fontSize: '12px' }}>{renderText(content.designConsiderations.split('\n\n')[0])}</span>
                </div>
              )}
              {content.governanceBullets?.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Governance Requirements</div>
                  <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {content.governanceBullets.map((b, i) => (
                      <li key={i} style={{ fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.5 }}>{b}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}

          {/* DB fallback — shown when no content file (e.g. Tenant10k packages) */}
          {!hasContent && (
            <>
              {pkg.mandate && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Mandate</div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.6 }}>{pkg.mandate}</p>
                </div>
              )}
              {pkg.behaviorDescription && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>How It Works</div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.6 }}>{pkg.behaviorDescription}</p>
                </div>
              )}
              {pkg.applicability && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'rgb(var(--color-text-muted))', marginBottom: '4px' }}>Applicability</div>
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgb(var(--color-text))', lineHeight: 1.6 }}>{pkg.applicability}</p>
                </div>
              )}
            </>
          )}

          {/* Not recommended — shown from content or DB */}
          {notRec && (
            <div className="modifier-manager-warning">
              <strong style={{ display: 'block', marginBottom: '2px', fontSize: '12px' }}>Not recommended for</strong>
              <span style={{ fontSize: '12px' }}>{renderText(notRec)}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modifier-manager-footer" style={{ padding: '12px 20px' }}>
          <button type="button" className="secondary-button" onClick={onClose}>Cancel</button>
          <button type="button" className="primary-button" onClick={onDeploy}>Deploy →</button>
        </div>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @param {{
 *   onPackageSelect: (pkg: object) => void,
 *   fiscalPeriodId?: number,
 *   onDeploySuccess?: () => void,
 *   className?: string,
 * }} props
 */
export default function PlanLibraryBrowser({ onPackageSelect, fiscalPeriodId, onDeploySuccess, className = '' }) {
  const navigate = useNavigate();

  const [packages, setPackages]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);
  const [search, setSearch]             = useState('');
  const [motionFilter, setMotionFilter] = useState('');
  const [sortCol, setSortCol]           = useState('sortOrder');   // active sort column
  const [sortDir, setSortDir]           = useState('asc');         // 'asc' | 'desc'
  const [contentMap, setContentMap]     = useState({});
  const [detailPkg, setDetailPkg]       = useState(null);

  const abortRef = useRef(null);

  // ── Sort handler ─────────────────────────────────────────────────────────
  const handleSort = useCallback((col) => {
    setSortCol((prev) => {
      if (prev === col) {
        setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
        return col;
      }
      setSortDir('asc');
      return col;
    });
  }, []);

  // ── Fetch packages ───────────────────────────────────────────────────────
  const fetchPackages = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const url = fiscalPeriodId
        ? `/api/v1/template-library/packages?fiscal_period_id=${fiscalPeriodId}`
        : '/api/v1/template-library/packages';
      const response = await fetch(url, { credentials: 'include', signal: controller.signal });
      if (!response.ok) throw new Error(`Unable to load plan library (${response.status}).`);
      const payload = await response.json();
      if (controller.signal.aborted) return;
      const raw = Array.isArray(payload) ? payload : payload.packages ?? payload.items ?? [];
      setPackages(raw.map(normalizePackage).filter((p) => p.packageCode));
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Unable to load plan library.');
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [fiscalPeriodId]);

  useEffect(() => {
    fetchPackages();
    return () => { abortRef.current?.abort(); };
  }, [fetchPackages]);

  const handleDeploySuccess = useCallback(() => {
    fetchPackages();
    onDeploySuccess?.();
  }, [fetchPackages, onDeploySuccess]);

  // ── Load markdown content (optional narrative enrichment) ─────────────────
  // Uses slug from API response to derive filename: {slug}-package.md
  // Drop a file in ui/src/content/plan-library/ and it loads automatically.
  // No UI changes needed. Missing file → graceful DB fallback in detail panel.
  useEffect(() => {
    if (packages.length === 0) return;
    packages.forEach(async (pkg) => {
      const slug = pkg._raw?.slug ?? pkg.packageCode;
      if (!slug) return;
      const parsed = await loadPackageContent(slug);
      if (parsed) {
        setContentMap((prev) => ({ ...prev, [pkg.packageCode]: parsed }));
      }
    });
  }, [packages]);

  // ── Derived values ───────────────────────────────────────────────────────
  // All primary display fields sourced from API (ref_plan_package).
  // Markdown content is optional detail panel enrichment only.
  const enrichedPackages = packages.map((pkg) => {
    const content = contentMap[pkg.packageCode];
    return {
      ...pkg,
      _content:          content,
      _displayName:      pkg.packageName || pkg.packageCode,
      _salesMotion:      pkg.salesMotion ?? content?.salesMotion ?? null,
      _moduleChips:      pkg.planTypeChips.length > 0 ? pkg.planTypeChips : (content?.moduleChips ?? []),
      _salesMotionGroup: pkg.salesMotionGroup ?? null,
    };
  });

  // ── Filter ───────────────────────────────────────────────────────────────
  const filteredPackages = enrichedPackages.filter((pkg) => {
    if (motionFilter && pkg._salesMotion !== motionFilter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return [pkg._displayName, pkg.packageCode, pkg._salesMotion, pkg.tier, ...(pkg._moduleChips)]
      .filter(Boolean).join(' ').toLowerCase().includes(q);
  });

  // ── Sort within each group ────────────────────────────────────────────────
  const SORT_ACCESSORS = {
    sortOrder:    (p) => p.sortOrder ?? 999,
    _displayName: (p) => (p._displayName ?? '').toLowerCase(),
    _salesMotion: (p) => (p._salesMotion ?? '').toLowerCase(),
    tier:         (p) => (p.tier ?? '').toLowerCase(),
    instanceCount:(p) => p.instanceCount ?? 0,
  };

  function sortPkgs(pkgs) {
    const accessor = SORT_ACCESSORS[sortCol] ?? SORT_ACCESSORS.sortOrder;
    return [...pkgs].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }

  const grouped = groupPackages(filteredPackages);
  const allMotions = [...new Set(enrichedPackages.map((p) => p._salesMotion).filter(Boolean))].sort();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', padding: '16px 24px', borderBottom: '1px solid rgb(var(--color-border))', flexShrink: 0 }}>
        <div>
          <button type="button" className="text-button" style={{ paddingLeft: 0, marginBottom: '4px' }} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'rgb(var(--color-text))', lineHeight: 1.3 }}>Plan Library</h1>
          <p style={{ margin: '2px 0 0', fontSize: '12px', color: 'rgb(var(--color-text-muted))' }}>
            Browse and deploy data-fit Curated compensation packages for this fiscal period.
          </p>
        </div>

        {/* Search + motion filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0, paddingTop: '24px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="search"
              aria-label="Search plan library"
              placeholder="Search packages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: '220px', padding: '6px 10px 6px 30px', border: '1px solid rgb(var(--color-border))', borderRadius: '8px', background: 'rgb(var(--color-bg))', color: 'rgb(var(--color-text))', fontSize: '12px' }}
            />
            <span style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: 'rgb(var(--color-text-muted))', pointerEvents: 'none' }}>🔍</span>
          </div>
          <select
            value={motionFilter}
            aria-label="Filter by sales motion"
            onChange={(e) => setMotionFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid rgb(var(--color-border))', borderRadius: '8px', background: 'rgb(var(--color-bg))', color: 'rgb(var(--color-text))', fontSize: '12px' }}
          >
            <option value="">All motions</option>
            {allMotions.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          {(search || motionFilter) && (
            <button type="button" className="text-button" style={{ fontSize: '11px' }} onClick={() => { setSearch(''); setMotionFilter(''); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

        {!loading && error && (
          <div role="alert" className="modifier-manager-inline-error" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>{error}</span>
            <button type="button" className="text-button" onClick={fetchPackages}>Retry</button>
          </div>
        )}

        {!loading && !error && packages.length === 0 && (
          <div className="modifier-manager-empty-state">
            <h4>No certified packages available</h4>
            <p>No certified packages are available in this environment.</p>
          </div>
        )}

        {!loading && !error && packages.length > 0 && filteredPackages.length === 0 && (
          <div className="modifier-manager-empty-state">
            <h4>No matches</h4>
            <p>No packages match the current search or filter.</p>
            <button type="button" className="text-button" style={{ paddingLeft: 0 }} onClick={() => { setSearch(''); setMotionFilter(''); }}>
              Clear filters
            </button>
          </div>
        )}

        {/* Motion-grouped sortable tables */}
        {(loading || (!error && grouped.length > 0)) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            {loading ? (
              <div className="modifier-manager-table-wrapper">
                <table className="modifier-manager-table">
                  <thead>
                    <tr>
                      <th>Package Name</th><th>Sales Motion</th><th>Plan Types</th>
                      <th>Tier</th><th>Status</th><th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>{[0, 1, 2, 3].map((i) => <SkeletonRow key={i} />)}</tbody>
                </table>
              </div>
            ) : (
              grouped.map(([group, pkgs]) => (
                <section key={group} aria-labelledby={`plb-group-${group}`}>
                  {/* Group heading */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <h2
                      id={`plb-group-${group}`}
                      style={{ margin: 0, fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))' }}
                    >
                      {group}
                    </h2>
                    <span style={{ fontSize: '10px', color: 'rgb(var(--color-text-muted))' }}>
                      {pkgs.length} {pkgs.length === 1 ? 'package' : 'packages'}
                    </span>
                  </div>

                  {/* Sortable table */}
                  <div className="modifier-manager-table-wrapper">
                    <table className="modifier-manager-table">
                      <thead>
                        <tr>
                          <SortTh label="Package Name" col="_displayName" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          <SortTh label="Sales Motion"  col="_salesMotion" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          <th>Plan Types</th>
                          <SortTh label="Tier"          col="tier"         sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          <SortTh label="Status"        col="instanceCount" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortPkgs(pkgs).map((pkg) => (
                          <tr
                            key={pkg.packageCode}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setDetailPkg(pkg)}
                            tabIndex={0}
                            role="button"
                            aria-label={`View details for ${pkg._displayName}`}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailPkg(pkg); } }}
                          >
                            {/* Package Name */}
                            <td style={{ padding: '12px 14px' }}>
                              <div style={{ fontWeight: 600, fontSize: '13px', color: 'rgb(var(--color-text))' }}>
                                {pkg._displayName}
                              </div>
                              {pkg._content?.similarTo && (
                                <div style={{ fontSize: '11px', color: 'rgb(var(--color-text-muted))', marginTop: '1px' }}>
                                  Similar to: {pkg._content.similarTo}
                                </div>
                              )}
                            </td>

                            {/* Sales Motion */}
                            <td style={{ padding: '12px 14px', fontSize: '12px', color: 'rgb(var(--color-text-muted))' }}>
                              {pkg._salesMotion ?? '—'}
                            </td>

                            {/* Plan Types */}
                            <td style={{ padding: '12px 14px' }}>
                              {pkg._moduleChips.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                  {pkg._moduleChips.slice(0, 3).map((chip) => (
                                    <span key={chip} className="modifier-manager-accelerator-badge">{chip}</span>
                                  ))}
                                  {pkg._moduleChips.length > 3 && (
                                    <span style={{ fontSize: '10px', color: 'rgb(var(--color-text-muted))' }}>
                                      +{pkg._moduleChips.length - 3} more
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span style={{ fontSize: '11px', color: 'rgb(var(--color-text-muted))' }}>—</span>
                              )}
                            </td>

                            {/* Tier */}
                            <td style={{ padding: '12px 14px' }}>
                              <span className="modifier-manager-badge modifier-manager-badge--not-created">
                                {String(pkg.tier ?? 'Standard').toUpperCase()}
                              </span>
                            </td>

                            {/* Status */}
                            <td style={{ padding: '12px 14px' }}>
                              <StatusBadge instanceCount={pkg.instanceCount} />
                            </td>

                            {/* Actions */}
                            <td style={{ padding: '12px 14px' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="primary-button"
                                style={{ fontSize: '11px', padding: '5px 10px' }}
                                onClick={(e) => { e.stopPropagation(); onPackageSelect(pkg); }}
                                aria-label={`Deploy ${pkg._displayName}`}
                              >
                                Deploy
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              ))
            )}
          </div>
        )}
      </div>

      {/* Detail panel */}
      {detailPkg && (
        <PackageDetailPanel
          pkg={detailPkg}
          content={contentMap[detailPkg.packageCode] ?? null}
          onClose={() => setDetailPkg(null)}
          onDeploy={() => { setDetailPkg(null); onPackageSelect(detailPkg); }}
        />
      )}
    </div>
  );
}

/*
 * ---------------------------------------------------------------------------
 * Document reconciliation (mandatory per S872 MANDATORY DOCUMENT PRODUCTION RULE)
 * ---------------------------------------------------------------------------
 * Input  : PlanLibraryBrowser.jsx v2.3.0 (S951 sortable/searchable) -- 824 lines
 * Output : PlanLibraryBrowser.jsx v2.4.0 (S951 glob loader + OTE removed) -- ~820 lines
 * Delta  : -~4L net. Surgical str_replace patches only.
 *
 * Changes S951 v2.4.0:
 *
 *   CONTENT_MAP → import.meta.glob (fully generic, zero hardcoding):
 *     Replaced static CONTENT_MAP dict + per-package import() entries with
 *     a single Vite import.meta.glob covering all *-package.md files in
 *     ui/src/content/plan-library/. Added loadPackageContent(slug) async
 *     function that derives the glob key from ref_plan_package.slug (returned
 *     by API). useEffect updated to call loadPackageContent(slug).
 *     Drop a {slug}-package.md file in the content folder → loads automatically.
 *     No UI changes ever needed for new packages.
 *     Missing file → null → detail panel renders DB fallback gracefully.
 *     No path or technical detail surfaced to user when file absent.
 *
 *   OTE REMOVED from PackageDetailPanel and normalizePackage:
 *     OTE min/midpoint/max/currency display removed. OTE is HRIS domain.
 *     certifiedOte* fields remain in API response for deploy modal pre-fill.
 *
 * Carried from v2.3.0:
 *   - Sortable columns (SortTh, SORT_ACCESSORS, sortPkgs)
 *   - Scrollable body, chip-aware search, Clear button
 *   - Detail panel DB fallback (mandate, behaviorDescription, applicability)
 *   - MOTION_CODE_TO_GROUP, MOTION_LABELS
 *
 * Carried from v2.2.0:
 *   - normalizePackage reads displayName, motionCode, planTypeChips
 *   - planTypeChips derived server-side by router from modifier_seed_config
 *
 * Carried from v2.1.0:
 *   - fiscalPeriodId prop, handleDeploySuccess refetch
 *
 * Authority: cpl_implementation_spec_v2_2_s869.md section UI Component Contracts
 * Session: S951
 * ---------------------------------------------------------------------------
 */
